/**
 * RecurrenceOverrideScopePicker (Plan 03-08).
 *
 * 3-mode scope picker for recurring event overrides per D-09 / UI-SPEC §7.
 * Only rendered when the user is editing a specific occurrence of a
 * recurring series (`mode.isOccurrence === true` in EventEditSheet).
 *
 * Chips: Bare denne / Fra og med denne / Hele serien (single-select).
 * Default = 'single' (least surprising — edit this one only).
 *
 * A11y: fieldset + legend (a11y contract in UI-SPEC §Accessibility).
 * T-CAL-37: scope selection is UI-only; API enforces access independently.
 *
 * Utseende (#157, steg 7b): `Chip`-primitiven. Valget er fortsatt ett av
 * tre; chipen som er valgt, er fylt og har aria-pressed. Spørsmålet står som
 * vanlig fet tekst, ikke som versal etikett, fordi det er en hel setning.
 */
import { useTranslation } from 'react-i18next';
import { Chip } from '../../components/primitives';

export type Scope = 'single' | 'from_this' | 'series';

export function RecurrenceOverrideScopePicker({
  value,
  onChange,
}: {
  value: Scope;
  onChange: (s: Scope) => void;
}) {
  const { t } = useTranslation();

  const opts: Array<{ id: Scope; key: string }> = [
    { id: 'single', key: 'calendar.override.scope.single' },
    { id: 'from_this', key: 'calendar.override.scope.fromThis' },
    { id: 'series', key: 'calendar.override.scope.series' },
  ];

  return (
    <fieldset data-testid="recurrence-scope-picker" className="m-0 border-0 p-0">
      <legend className="m-0 mb-2.5 block p-0 text-body-s font-bold text-ink">
        {t('calendar.override.heading')}
      </legend>
      <div className="flex flex-wrap gap-2">
        {opts.map((o) => (
          <Chip
            key={o.id}
            selected={value === o.id}
            onClick={() => onChange(o.id)}
            data-testid={`override-scope-${o.id}`}
          >
            {t(o.key)}
          </Chip>
        ))}
      </div>
    </fieldset>
  );
}
